import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, ShieldCheck, Play } from 'lucide-react';
import { Button } from './ui/button';
import { HERO_STATS, SIGNUP_URL, WHATSAPP_URL, BRAND } from '../data/mock';
import VideoModal from './VideoModal';

export default function Hero() {
  const [videoOpen, setVideoOpen] = useState(false);
  return (
    <section className="relative overflow-hidden hero-grid">
      <div className="absolute inset-0 bg-gradient-to-b from-white/60 via-white/80 to-white pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-24">
        <div className="flex flex-wrap items-center justify-center gap-3 mb-8 fade-up">
          <span className="chip">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            Powered by Official WhatsApp Business API
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium">
            <ShieldCheck size={14} /> Meta Business Partner
          </span>
        </div>

        <h1 className="text-center text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 max-w-5xl mx-auto leading-[1.05] fade-up">
          Business Communication,{' '}
          <span className="brush-underline text-brand-purple">Reimagined</span>
          <br />
          for Serious Businesses.
        </h1>
        <p className="mt-6 text-center text-lg text-slate-600 max-w-2xl mx-auto fade-up">
          ACTIWA is the AI-powered WhatsApp automation platform trusted by 500+ brands.
          Launch in under an hour — no code, no markup fees.
        </p>

        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 fade-up">
          <a href={SIGNUP_URL} target="_blank" rel="noreferrer">
            <Button size="lg" className="bg-brand-purple hover:bg-brand-purple-dark text-white rounded-full px-7 h-12">
              Start Free Trial <ArrowRight className="ml-2" size={18} />
            </Button>
          </a>
          <Button
            size="lg"
            variant="outline"
            onClick={() => setVideoOpen(true)}
            className="rounded-full px-7 h-12 border-slate-300"
          >
            <Play size={16} className="mr-2 text-brand-green fill-brand-green" /> Watch Demo
          </Button>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-600">
          {['No credit card required', '7-day free trial', '24/7 support'].map((t) => (
            <span key={t} className="inline-flex items-center gap-1.5">
              <CheckCircle2 size={16} className="text-emerald-500" /> {t}
            </span>
          ))}
        </div>

        {/* Mock product illustration */}
        <div className="relative mt-14 max-w-5xl mx-auto">
          <div className="rounded-3xl bg-gradient-to-br from-brand-purple to-brand-purple-dark p-1 shadow-2xl shadow-brand-purple/20">
            <div className="rounded-[22px] bg-brand-purple relative overflow-hidden">
              <div className="grid md:grid-cols-2 gap-6 p-8 md:p-12">
                <div className="text-white">
                  <p className="text-white/80 text-sm font-semibold uppercase tracking-wider">Complete WhatsApp</p>
                  <h3 className="mt-1 text-3xl md:text-4xl font-extrabold leading-tight">
                    Marketing & Chatbot Platform
                  </h3>
                  <p className="mt-3 text-white/80 text-sm">Powerful Automation & Engagement for WhatsApp Growth</p>
                  <div className="mt-6 grid grid-cols-2 gap-3">
                    {['No-Code Chatbot', 'Shared Team Inbox', 'Bulk Broadcast', 'WhatsApp Automation', 'AI Assistant', 'CRM Integration'].map((f) => (
                      <div key={f} className="bg-white/10 backdrop-blur rounded-lg px-3 py-2 text-xs font-medium border border-white/10">
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Chat with ACTIWA on WhatsApp"
                  className="relative block bg-white rounded-2xl p-5 shadow-xl hover:shadow-2xl transition-shadow group"
                >
                  <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                    <div className="w-10 h-10 rounded-full bg-brand-purple-light grid place-items-center text-brand-purple font-bold">A</div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900">ACTIWA</p>
                      <p className="text-[11px] text-brand-green flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-green" />
                        Business Account • Online
                      </p>
                    </div>
                    <span className="text-[10px] text-slate-400">+91 {BRAND.phone}</span>
                  </div>
                  <div className="mt-4 space-y-3">
                    <div className="max-w-[80%] bg-slate-100 rounded-2xl rounded-tl-sm px-3 py-2 text-sm text-slate-800">
                      Hi! I'm interested in your product.
                      <div className="text-[10px] text-slate-500 mt-1">10:30 AM</div>
                    </div>
                    <div className="max-w-[80%] ml-auto bg-brand-green text-white rounded-2xl rounded-tr-sm px-3 py-2 text-sm">
                      Hello! Thanks for reaching out. How can I help you today?
                      <div className="text-[10px] text-green-50 mt-1">10:30 AM ✓✓</div>
                    </div>
                    <div className="max-w-[80%] ml-auto bg-brand-green text-white rounded-2xl rounded-tr-sm px-3 py-2 text-sm">
                      Would you like a quick demo?
                      <div className="text-[10px] text-green-50 mt-1">10:30 AM ✓✓</div>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 bg-slate-50 rounded-full px-4 py-2.5 border border-slate-100 group-hover:border-brand-green group-hover:bg-brand-green/5 transition-colors">
                    <span className="flex-1 text-xs text-slate-500 truncate">Type a message... (opens WhatsApp)</span>
                    <span className="w-8 h-8 rounded-full bg-brand-green grid place-items-center text-white shrink-0 group-hover:scale-110 transition-transform">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
                        <path d="M2 21l1.65-4.5A9 9 0 1 1 7.5 20.35L3 22l-1-1zm5.66-2.11l.4.24a7 7 0 1 0-2.19-2.19l.24.4-.83 2.38 2.38-.83z"/>
                      </svg>
                    </span>
                  </div>
                  <div className="absolute -top-3 -right-3 bg-white shadow-md rounded-xl px-3 py-2 flex items-center gap-2 border border-slate-100">
                    <ShieldCheck size={16} className="text-brand-purple" />
                    <div>
                      <p className="text-[11px] font-semibold text-slate-900 leading-none">Meta Partner</p>
                      <p className="text-[10px] text-slate-500">Verified</p>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
            {HERO_STATS.map((s) => (
              <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-5 text-center card-lift">
                <p className="text-2xl md:text-3xl font-extrabold text-slate-900">{s.value}</p>
                <p className="mt-1 text-sm text-slate-500">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />
    </section>
  );
}
